import React from 'react';
import { Box, Card, Grid, Stack, Typography, alpha } from '@mui/material';
import { Icon } from '@iconify/react';
import { formatMoney } from 'src/services/payroll/calculatePay';

interface Stat {
  label: string;
  value: string | number;
  hint?: string;
  color: string;
  accent?: string;
}

export function PaySummaryCards({
  matched,
  problems,
  warnings,
  totalGross,
  totalTax,
  totalNet,
}: {
  matched: number;
  problems: number;
  warnings?: number;
  totalGross: number;
  totalTax: number;
  totalNet: number;
}) {
  const stats: Stat[] = [
    {
      label: 'Ready to pay',
      value: matched,
      hint: warnings ? `${warnings} name warning${warnings === 1 ? '' : 's'}` : 'Matched students',
      color: '#297041',
      accent: '#E7F7EF',
    },
    {
      label: 'Needs attention',
      value: problems,
      hint: problems === 0 ? 'All clear' : 'Skipped on approve',
      color: problems > 0 ? '#C05621' : '#718096',
      accent: problems > 0 ? '#FFFAF0' : '#F7FAFC',
    },
    {
      label: 'Total gross',
      value: formatMoney(totalGross),
      hint: 'Sum of period %',
      color: '#2B6CB0',
      accent: '#EBF8FF',
    },
    {
      label: 'Tax withheld',
      value: formatMoney(totalTax),
      hint: 'Recorded for Tax Day',
      color: '#805AD5',
      accent: '#FAF5FF',
    },
    {
      label: 'Total net',
      value: formatMoney(totalNet),
      hint: 'Scholar Dollars to deposit',
      color: '#EDB42E',
      accent: '#FFFBEB',
    },
  ];

  return (
    <Grid container spacing={2}>
      {stats.map((stat) => (
        <Grid item xs={12} sm={6} md={stats.length > 4 ? 2.4 : 3} key={stat.label}>
          <Card
            elevation={0}
            sx={{
              p: 2.25,
              height: '100%',
              borderRadius: 3,
              border: '1px solid',
              borderColor: alpha(stat.color, 0.18),
              background: `linear-gradient(145deg, ${stat.accent} 0%, #FFFFFF 70%)`,
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 8px 24px ${alpha(stat.color, 0.12)}`,
              },
            }}
          >
            <Stack spacing={0.75}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  fontSize: 11,
                }}
              >
                {stat.label}
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, color: stat.color, lineHeight: 1.2 }}>
                {stat.value}
              </Typography>
              {stat.hint && (
                <Typography variant="caption" color="text.secondary">
                  {stat.hint}
                </Typography>
              )}
            </Stack>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

export function WizardStepper({
  steps,
  activeStep,
}: {
  steps: string[];
  activeStep: number;
}) {
  return (
    <Box sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
        {steps.map((label, index) => {
          const done = index < activeStep;
          const active = index === activeStep;
          return (
            <React.Fragment key={label}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    display: 'grid',
                    placeItems: 'center',
                    fontSize: 13,
                    fontWeight: 700,
                    color: done || active ? '#fff' : '#718096',
                    bgcolor: done ? '#297041' : active ? '#EDB42E' : '#E2E8F0',
                    boxShadow: active ? '0 0 0 4px rgba(237,180,46,0.2)' : 'none',
                    transition: 'all 0.2s ease',
                  }}
                >
                  {done ? <Icon icon="solar:check-read-bold" width={16} /> : index + 1}
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: active ? 700 : 500,
                    color: active ? 'text.primary' : 'text.secondary',
                  }}
                >
                  {label}
                </Typography>
              </Stack>
              {index < steps.length - 1 && (
                <Box
                  sx={{
                    width: { xs: 16, sm: 32 },
                    height: 2,
                    bgcolor: index < activeStep ? '#297041' : '#E2E8F0',
                    borderRadius: 1,
                  }}
                />
              )}
            </React.Fragment>
          );
        })}
      </Stack>
    </Box>
  );
}
